import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react';

import { FUNNEL_LABEL } from '../BriefDetailsFields.tsx';

import {
  createIdeaBatch,
  fetchContentList,
  fetchSettings,
  ideaBatchPath,
  type IdeaBatchInput,
  isDutchLanguage,
  type PlanningRow,
  previewPlanningTable,
} from '../api.ts';
import {
  CampaignField,
  FALLBACK_LANGUAGES,
  LanguageField,
  RepositoryChoice,
  useProfileLanguage,
  useCampaignChoice,
  useRepositoryOrder,
} from '../contentFields.tsx';
import { describeError, useAppData } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { ideaLines, looksLikeTable } from '../ideaText.ts';
import { Link, navigate } from '../router.tsx';
import { useToast } from '../toast.tsx';
import { Badge, Loader, Notice, PageHeader, Panel, Skeleton } from '../ui.tsx';
import * as wording from '../wording.ts';

/**
 * Starting a batch of ideas (US-122): a title, the product it is for, and
 * the ideas themselves — typed one per line, or a whole ideas document — or a
 * theme for Claude to suggest them from.
 *
 * The repositories and the language are picked exactly as on the New content
 * form — the pieces are shared — because every idea that is approved becomes a
 * content with this batch's repositories and language.
 */

/** The server's ceiling on a batch, typed or suggested. */
const MAX_IDEAS = 50;
const MIN_SUGGESTED = 1;
const DEFAULT_SUGGESTED = 10;
/** The server's ceilings on the title and the theme. */
const MAX_TITLE_LENGTH = 200;
const MAX_THEME_LENGTH = 2000;
/** The server's ceiling on an ideas document, in characters; it has none on lines. */
const MAX_DOCUMENT_LENGTH = 300_000;

type Source = 'typed' | 'document' | 'suggested';

/** How long the form waits after the last keystroke before it asks for a table preview. */
const PREVIEW_DELAY_MS = 300;

/** The server's reading of a pasted table: still being asked for, the rows, or why it was refused. */
type TablePreview =
  | { readonly state: 'reading' }
  | { readonly state: 'read'; readonly rows: readonly PlanningRow[] }
  | { readonly state: 'refused'; readonly message: string };

export function NewIdeaBatch() {
  const { repositories } = useAppData();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const { chosen, toggle, makePrimary } = useRepositoryOrder();
  const [languages, setLanguages] = useState<readonly string[]>(FALLBACK_LANGUAGES);
  const [defaultLanguage, setDefaultLanguage] = useState<string | null>(null);
  const primary = chosen[0] ?? null;
  const languageChoice = useProfileLanguage(primary, defaultLanguage);
  const campaignChoice = useCampaignChoice();
  const [source, setSource] = useState<Source>('typed');
  const [typed, setTyped] = useState('');
  const [documentText, setDocumentText] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const [theme, setTheme] = useState('');
  const [count, setCount] = useState(String(DEFAULT_SUGGESTED));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isTable = looksLikeTable(typed);
  const [preview, setPreview] = useState<TablePreview>({ state: 'reading' });

  // A pasted table is read by the server, so the preview and the batch agree.
  useEffect(() => {
    if (!isTable) return undefined;
    setPreview({ state: 'reading' });
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      previewPlanningTable(typed, controller.signal)
        .then((rows) => {
          setPreview({ state: 'read', rows });
        })
        .catch((cause: unknown) => {
          if (!controller.signal.aborted) setPreview({ state: 'refused', message: describeError(cause) });
        });
    }, PREVIEW_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [isTable, typed]);

  // The languages the server offers and the install's default one, read once.
  // Neither is needed to submit: without them the form falls back to its own.
  useEffect(() => {
    const controller = new AbortController();
    fetchContentList(controller.signal)
      .then((list) => {
        if (list.languages !== undefined && list.languages.length > 0) setLanguages(list.languages);
      })
      .catch(() => undefined);
    fetchSettings(controller.signal)
      .then((settings) => {
        setDefaultLanguage(settings.defaultLanguage);
      })
      .catch(() => undefined);
    return () => {
      controller.abort();
    };
  }, []);

  const usable = (repositories ?? []).filter((repository) => repository.keyConfigured);
  const lines = ideaLines(typed);
  const tooMany = !isTable && lines.length > MAX_IDEAS;
  const tableReady = preview.state === 'read' && preview.rows.length > 0;
  const tableRefused = isTable && preview.state === 'refused';
  const documentLines = documentText === '' ? 0 : documentText.split('\n').length;
  const documentTooLong = documentText.length > MAX_DOCUMENT_LENGTH;
  const requested = Number(count);
  const countValid = Number.isInteger(requested) && requested >= MIN_SUGGESTED && requested <= MAX_IDEAS;
  const ready =
    title.trim() !== '' &&
    chosen.length > 0 &&
    (source === 'typed'
      ? isTable
        ? tableReady
        : lines.length > 0 && !tooMany
      : source === 'document'
        ? documentText.trim() !== '' && !documentTooLong
        : theme.trim() !== '' && countValid);

  const openFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    file
      .text()
      .then((text) => {
        setDocumentText(text);
        if (title.trim() === '') setTitle(file.name.replace(/\.[^.]+$/, '').slice(0, MAX_TITLE_LENGTH));
      })
      .catch((cause: unknown) => {
        setError(`${wording.DOCUMENT_FILE_FAILED} ${describeError(cause)}`);
      });
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!ready || busy) return;
    setError(null);
    const { language, address } = languageChoice;
    const input: IdeaBatchInput = {
      title: title.trim(),
      repositories: chosen.map((repositoryId, index) => ({
        repositoryId,
        role: index === 0 ? 'primary' : 'context',
      })),
      // The language as the operator saw it on screen, and for Dutch the
      // address when one was chosen; an unchosen one is the profile's to give.
      language,
      ...(isDutchLanguage(language) && address !== '' ? { address } : {}),
      ...(campaignChoice.campaignId === '' ? {} : { campaignId: campaignChoice.campaignId }),
      ...(source === 'suggested'
        ? { theme: theme.trim(), requestedCount: requested }
        : source === 'document'
          ? { document: documentText }
          : isTable
            ? { table: typed }
            : { seeds: lines }),
    };
    setBusy(true);
    createIdeaBatch(input)
      .then((batch) => {
        toast.ok(wording.BATCH_STARTED);
        navigate(ideaBatchPath(batch.id));
      })
      .catch((cause: unknown) => {
        setError(describeError(cause));
        setBusy(false);
      });
  };

  return (
    <div className="page page--narrow">
      <PageHeader
        back={{ href: '/ideas', label: wording.IDEAS }}
        title={wording.NEW_BATCH}
        subtitle={wording.NEW_BATCH_SUBTITLE}
      />

      <div className="grid grid--form">
        <form className="panel" onSubmit={submit}>
          <div className="panel__body form">
            {repositories === null ? (
              <Skeleton lines={6} />
            ) : (
              <>
                <div className="field">
                  <label className="field__label" htmlFor="batch-title">
                    {wording.BATCH_TITLE}
                  </label>
                  <input
                    id="batch-title"
                    className="field__input"
                    value={title}
                    onChange={(event) => {
                      setTitle(event.target.value);
                    }}
                    placeholder={wording.BATCH_TITLE_PLACEHOLDER}
                    autoComplete="off"
                    autoFocus
                    maxLength={MAX_TITLE_LENGTH}
                    aria-describedby="batch-title-hint"
                  />
                  <p className="field__hint" id="batch-title-hint">
                    {wording.BATCH_TITLE_HINT}
                  </p>
                </div>

                <div className="field">
                  <span className="field__label">{wording.REPOSITORIES}</span>
                  {usable.length === 0 ? (
                    <Notice kind="info">
                      {wording.BATCH_NEEDS_REPOSITORY}{' '}
                      <Link className="link" href="/repositories">
                        {wording.ADD_ONE}
                      </Link>{' '}
                      {wording.BATCH_NEEDS_REPOSITORY_END}
                    </Notice>
                  ) : (
                    <ul className="choices">
                      {usable.map((repository) => (
                        <RepositoryChoice
                          key={repository.id}
                          repository={repository}
                          position={chosen.indexOf(repository.id)}
                          onToggle={() => {
                            toggle(repository.id);
                          }}
                          onMakePrimary={() => {
                            makePrimary(repository.id);
                          }}
                        />
                      ))}
                    </ul>
                  )}
                  <p className="field__hint">{wording.BATCH_REPOSITORIES_HINT}</p>
                </div>

                <LanguageField
                  id="batch-language"
                  choice={languageChoice}
                  languages={languages}
                  hint={wording.BATCH_LANGUAGE_HINT}
                />

                <CampaignField id="batch-campaign" choice={campaignChoice} hint={wording.CAMPAIGN_FIELD_HINT} />

                <div className="field">
                  <span className="field__label">{wording.WHERE_IDEAS_COME_FROM}</span>
                  <div className="field__actions" role="radiogroup" aria-label={wording.WHERE_IDEAS_COME_FROM}>
                    {(
                      [
                        ['typed', wording.I_HAVE_IDEAS],
                        ['document', wording.I_HAVE_A_DOCUMENT],
                        ['suggested', wording.SUGGEST_IDEAS],
                      ] as const
                    ).map(([value, label]) => (
                      <label className="radio" key={value}>
                        <input
                          type="radio"
                          name="batch-source"
                          value={value}
                          checked={source === value}
                          onChange={() => {
                            setSource(value);
                          }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {source === 'typed' ? (
                  <div className="field">
                    <label className="field__label" htmlFor="batch-ideas">
                      {wording.ONE_IDEA_PER_LINE}
                    </label>
                    <textarea
                      id="batch-ideas"
                      className="field__input field__textarea"
                      rows={8}
                      value={typed}
                      onChange={(event) => {
                        setTyped(event.target.value);
                      }}
                      placeholder={wording.IDEAS_PLACEHOLDER}
                      aria-describedby="batch-ideas-hint"
                      aria-invalid={tooMany || tableRefused}
                    />
                    <p
                      className={`field__hint${tooMany || tableRefused ? ' field__hint--error' : ''}`}
                      id="batch-ideas-hint"
                      aria-live="polite"
                    >
                      {!isTable
                        ? tooMany
                          ? wording.tooManyIdeas(lines.length, MAX_IDEAS)
                          : wording.ideaLines(lines.length, MAX_IDEAS)
                        : preview.state === 'read'
                          ? wording.tableRows(preview.rows.length)
                          : preview.state === 'refused'
                            ? preview.message
                            : wording.READING_TABLE}
                    </p>
                    {isTable && preview.state === 'read' && <TablePreviewRows rows={preview.rows} />}
                  </div>
                ) : source === 'document' ? (
                  <div className="field">
                    <label className="field__label" htmlFor="batch-document">
                      {wording.IDEAS_DOCUMENT}
                    </label>
                    <textarea
                      id="batch-document"
                      className="field__input field__textarea"
                      rows={16}
                      value={documentText}
                      onChange={(event) => {
                        setDocumentText(event.target.value);
                      }}
                      placeholder={wording.IDEAS_DOCUMENT_PLACEHOLDER}
                      spellCheck={false}
                      aria-describedby="batch-document-hint"
                      aria-invalid={documentTooLong}
                    />
                    <p
                      className={`field__hint${documentTooLong ? ' field__hint--error' : ''}`}
                      id="batch-document-hint"
                      aria-live="polite"
                    >
                      {wording.documentHint(documentLines, documentText.length, MAX_DOCUMENT_LENGTH)}
                    </p>
                    <div className="field__actions">
                      <input
                        ref={fileInput}
                        type="file"
                        accept=".md,.markdown,.txt,text/markdown,text/plain"
                        hidden
                        onChange={openFile}
                      />
                      <button
                        type="button"
                        className="button button--small"
                        onClick={() => {
                          fileInput.current?.click();
                        }}
                      >
                        <Icon name="file" />
                        {wording.OPEN_DOCUMENT_FILE}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="field">
                      <label className="field__label" htmlFor="batch-theme">
                        {wording.THEME}
                      </label>
                      <textarea
                        id="batch-theme"
                        className="field__input field__textarea"
                        rows={4}
                        value={theme}
                        onChange={(event) => {
                          setTheme(event.target.value);
                        }}
                        placeholder={wording.THEME_PLACEHOLDER}
                        maxLength={MAX_THEME_LENGTH}
                        aria-describedby="batch-theme-hint"
                      />
                      <p className="field__hint" id="batch-theme-hint">
                        {wording.THEME_HINT}
                      </p>
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="batch-count">
                        {wording.HOW_MANY}
                      </label>
                      <input
                        id="batch-count"
                        className="field__input field__input--narrow"
                        type="number"
                        inputMode="numeric"
                        min={MIN_SUGGESTED}
                        max={MAX_IDEAS}
                        step={1}
                        value={count}
                        onChange={(event) => {
                          setCount(event.target.value);
                        }}
                        aria-describedby="batch-count-hint"
                        aria-invalid={!countValid}
                      />
                      <p className="field__hint" id="batch-count-hint">
                        {wording.howManyHint(MIN_SUGGESTED, MAX_IDEAS)}
                      </p>
                    </div>
                  </>
                )}

                {error !== null && <Notice kind="error">{error}</Notice>}

                <div className="field__actions">
                  <button type="submit" className="button button--primary" disabled={busy || !ready}>
                    <Icon name="bulb" />
                    {busy ? wording.STARTING_BATCH : wording.START_BATCH}
                  </button>
                  <Link className="button button--quiet" href="/ideas">
                    {wording.CANCEL}
                  </Link>
                </div>
                {busy && <Loader label={wording.STARTING_BATCH} size="sm" />}
              </>
            )}
          </div>
        </form>

        <Panel title={wording.WHAT_HAPPENS_NEXT} icon="info" className="panel--aside">
          <ol className="steps steps--plain">
            {wording.BATCH_STEPS.map((step, index) => (
              <li className="step" key={step.title}>
                <span className="step__marker">{index + 1}</span>
                <div className="step__main">
                  <span className="step__title">{step.title}</span>
                  <span className="step__body">{step.body}</span>
                </div>
              </li>
            ))}
          </ol>
        </Panel>
      </div>
    </div>
  );
}

/** One line per pasted row: what each idea starts out with. */
function TablePreviewRows({ rows }: { readonly rows: readonly PlanningRow[] }) {
  return (
    <div className="table-wrap table-preview" aria-label={wording.TABLE_PREVIEW} role="region">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Title</th>
            <th scope="col">Length</th>
            <th scope="col">Funnel</th>
            <th scope="col">Angle</th>
            <th scope="col">Publish by</th>
            <th scope="col">Campaign</th>
            <th scope="col">Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const { goal, audience, placement, funnelStages, angle, extraDetails } = row.briefDetails;
            const details = [
              ...(goal === null ? [] : [`Goal: ${goal}`]),
              ...(audience === null ? [] : [`Audience: ${audience}`]),
              ...(placement === null ? [] : [`Placement: ${placement}`]),
              ...extraDetails.map(({ key, value }) => `${key}: ${value}`),
            ];
            return (
              <tr key={index}>
                <td>{row.title}</td>
                <td className="table__nowrap">{row.targetSeconds === null ? '—' : `${String(row.targetSeconds)} s`}</td>
                <td>
                  {funnelStages.length === 0 ? (
                    '—'
                  ) : (
                    <span className="cell-stack__badges">
                      {funnelStages.map((stage) => (
                        <Badge key={stage}>{FUNNEL_LABEL[stage]}</Badge>
                      ))}
                    </span>
                  )}
                </td>
                <td>{angle ?? '—'}</td>
                <td className="table__nowrap">{row.dueOn ?? '—'}</td>
                <td>
                  {row.campaign !== null
                    ? row.campaign.name
                    : row.unknownCampaign !== null
                      ? wording.unknownTableCampaign(row.unknownCampaign)
                      : '—'}
                </td>
                <td>{details.length === 0 ? '—' : details.join('; ')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
